import { patchState, signalStore, withMethods, withState } from "@ngrx/signals";
import { inject } from "@angular/core";
import { Customercontrollerv2Api, UpdateCreditCardRequest, FiscalInfo, CreditCard, CustomersApi } from "../../../api/proxies";
import { UpdatePaymentInfoRequest, IUpdatePaymentMethodState } from "./update-payment-method.model";
import { NotifyService } from "@ui/notify";
import { TranslocoService } from "@jsverse/transloco";
import { CardBankService } from "../../../card.service";
import { firstValueFrom } from "rxjs";

const initialState: IUpdatePaymentMethodState = {
    loading: false,
    error: null,
    customer: null,
};

export interface TokenizeCardParams {
    holderName: string;
    cardExpiration: string;
    cardNumber: string;
    cardCvv: string;
    cardFlag: string;
}

function checkCardExpirationError(error: any): boolean {
    try {
        if (error?.error?.errors && Array.isArray(error.error.errors)) {
            return error.error.errors.some(
                (err: any) => err.parameter === "card_expiration"
            );
        }
        if (error?.errors && Array.isArray(error.errors)) {
            return error.errors.some(
                (err: any) => err.parameter === "card_expiration"
            );
        }
    } catch {
        return false;
    }
    return false;
}

export const UpdatePaymentMethodStore = signalStore(
    withState(initialState),
    withMethods(
        (
            store,
            api = inject(Customercontrollerv2Api),
            customersApi = inject(CustomersApi),
            cardBankService = inject(CardBankService),
            notify = inject(NotifyService),
            translate = inject(TranslocoService)
        ) => ({

            async loadCustomerInfo() {
                patchState(store, { loading: true, error: null });

                try {
                    const customerInfo = await firstValueFrom(customersApi.info());
                    patchState(store, { customer: customerInfo, loading: false });
                    return customerInfo;
                } catch (error) {
                    patchState(store, { error: 'Erro ao carregar dados do cliente', loading: false });
                    return null;
                }
            },

            async tokenizeAndUpdatePaymentInfo(
                tokenizeParams: TokenizeCardParams,
                expirationMonth: string,
                expirationYear: string,
                fiscalInfo: any
            ) {
                patchState(store, { loading: true, error: null });

                try {
                    const tokenizeResult = await firstValueFrom(
                        cardBankService.tokenize(
                            tokenizeParams.holderName,
                            tokenizeParams.cardExpiration,
                            tokenizeParams.cardNumber,
                            tokenizeParams.cardCvv,
                            tokenizeParams.cardFlag
                        )
                    );

                    const creditCard = CreditCard.fromJS({
                        flag: tokenizeResult.flag,
                        token: tokenizeResult.token,
                        number: tokenizeResult.number,
                        expirationMonth: expirationMonth,
                        expirationYear: expirationYear,
                        securityCode: tokenizeParams.cardCvv,
                        ownerName: tokenizeParams.holderName
                    });

                    const fiscalInfoConverted = fiscalInfo ? FiscalInfo.fromJS(fiscalInfo) : undefined;
                    const updateRequest = new UpdateCreditCardRequest({
                        creditCard: creditCard,
                        fiscalInfo: fiscalInfoConverted
                    });
                    
                    await firstValueFrom(api.paymentInfo(updateRequest));
                    notify.success(translate.translate("update-payment-method-dialog.updateSuccess"));
                    return true;
                } catch (error: any) {
                    const hasCardExpirationError = checkCardExpirationError(error);
                    if (hasCardExpirationError) {
                        notify.error(translate.translate("update-payment-method-dialog.updateErrorCardData"));
                    } else {
                        notify.error(translate.translate("update-payment-method-dialog.updateError"));
                    }
                    return false;
                } finally {
                    patchState(store, { loading: false });
                }
            },
        })
    )
);
